import { type RootState } from '@/store';

import { USER_LOAN_PROGRAM } from '@/config/constants';
import { HOMEOWNERS_INSURANCE_RATE } from '@/utils/calculator/assumptions';
import { estimateStateClosingTaxes } from '@/utils/fees/stateClosingTaxes';
import { calculateTaxFactor } from './index';
import {
  calculateEnhancedInterestRate,
  getBridgeRateByTerm,
} from './rateIntegration';

interface BridgeLoanParams {
  offerPrice?: number;
  listPrice: number;
  downPayment?: number;
  zipCode?: number;
  hoaFees?: number;
  commission?: number;
  monthlyPropertyTaxes?: number;
  monthlyHomeownersInsurance?: number;
  marketInterestRate?: number;
  creditScore?: number;
  loanTerm?: number; // In months: 6, 12, 18, or 24
}

export const calculateBridgeClosingDisclosure = (
  {
    offerPrice,
    listPrice,
    downPayment = 0,
    zipCode = 0,
    hoaFees = 0,
    commission = 0,
    monthlyPropertyTaxes: monthlyPropertyTaxesOverride,
    monthlyHomeownersInsurance: monthlyHomeownersInsuranceOverride,
    marketInterestRate = 10.5,
    creditScore = 0,
    loanTerm = 12, // Default 12 months
  }: BridgeLoanParams,
  state?: RootState
) => {
  const purchasePrice = offerPrice || listPrice;
  const loanAmount = purchasePrice - downPayment;
  const ltv = loanAmount / purchasePrice;

  const taxFactor = calculateTaxFactor(zipCode);

  // Get Bridge rate by term from Redux state
  const interestRate = state
    ? getBridgeRateByTerm(state, loanTerm, creditScore, ltv)
    : marketInterestRate; // Fallback to base rate if no state provided

  // Bridge loans are interest-only
  const monthlyPayment = (loanAmount * interestRate) / 1200; // Annual rate / 12 months
  const balloonPayment = loanAmount; // Full principal due at end

  // Same lender and third-party fees as the other residential programs; the
  // 2% bridge fee below is what a bridge loan costs extra.
  const appraisalFeeBase = 650;
  const processingFeeBase = 795;
  const underwritingFee = 1295;
  const applicationFeeBase = 495;
  const creditReportFeeBase = 150;
  const titleSettlementAgentFee = 650;
  const countyRecordingFees = 200;
  const stateRecordingFees = 150;
  const recordingTitle = 0; // No separate title recording fee
  const recordingMortgage = 75;
  const recordingDeedBase = 75;
  const floodMonitoringFee = 25;
  const floodDeterminationFee = 35;
  const pestInspectionFee = 145;
  const surveyFee = 250;
  const insuranceBinder = 150;
  const earnestMoneyDeposit = 1000;
  const lenderCredits = 0;
  // Tax proration depends on the closing date; no flat credit is assumed.
  const prepaidsPropertyTaxes = 0;

  // Bridge loan specific fees
  const bridgeFee = loanAmount * 0.02; // 2% bridge fee
  // Residential bridge loans are priced with points, not an exit fee.
  const exitFee = 0;

  // Factor-based calculations
  const annualPropertyTax = (purchasePrice * taxFactor) / 100;
  const monthlyPropertyTaxesBase = annualPropertyTax / 12;
  const monthlyPropertyTaxes =
    monthlyPropertyTaxesOverride ?? monthlyPropertyTaxesBase;

  // Title insurance
  const titleTitleSearchBase = 250;
  // Lender's policy covers the loan amount.
  const titleLendersTitleInsuranceBase = loanAmount * 0.004;
  const optionalOwnersTitleInsuranceBase = purchasePrice * 0.00575;
  const minimumOwnersTitle = 1650;
  const maximumOwnersTitle = 5850;
  const optionalOwnersTitleInsurance = Math.min(
    Math.max(optionalOwnersTitleInsuranceBase, minimumOwnersTitle),
    maximumOwnersTitle
  );

  // Insurance and prepaids
  const monthlyHomeownersInsuranceBase =
    (purchasePrice * HOMEOWNERS_INSURANCE_RATE) / 12;
  const monthlyHomeownersInsurance =
    monthlyHomeownersInsuranceOverride ?? monthlyHomeownersInsuranceBase;
  const prepaidsHomeownersInsurance = monthlyHomeownersInsurance * 3;
  const initialEscrowTaxes = monthlyPropertyTaxes * 6;

  // Prepaid interest (15 days)
  const dailyInterestRate = interestRate / 365 / 100;
  const prepaidInterest = loanAmount * dailyInterestRate * 15;

  // Transfer taxes
  // Buyer's share of the state deed/transfer tax (by state custom) and the
  // borrower's loan taxes — see utils/fees/stateClosingTaxes.
  const stateTaxes = estimateStateClosingTaxes({
    zipCode,
    price: purchasePrice,
    loanAmount,
  });
  const transferTaxes = stateTaxes.transferTaxes;
  const mortgageTaxes = stateTaxes.mortgageTaxes;

  // Calculate totals
  const totalOriginationFees =
    underwritingFee +
    applicationFeeBase +
    processingFeeBase +
    creditReportFeeBase +
    bridgeFee;

  const totalOtherFees =
    appraisalFeeBase +
    titleSettlementAgentFee +
    floodDeterminationFee +
    floodMonitoringFee +
    pestInspectionFee +
    surveyFee +
    countyRecordingFees +
    stateRecordingFees +
    transferTaxes +
    mortgageTaxes +
    prepaidInterest +
    prepaidsHomeownersInsurance +
    // A stray semicolon used to end the sum here, silently dropping escrow,
    // title and recording costs from closing costs.
    initialEscrowTaxes +
    optionalOwnersTitleInsurance +
    insuranceBinder +
    titleTitleSearchBase +
    titleLendersTitleInsuranceBase +
    recordingTitle +
    recordingMortgage +
    recordingDeedBase;
  // exitFee is charged when the loan is paid off, not at closing; it is
  // returned separately for the estimate's disclosure.

  const totalLenderCosts = totalOriginationFees + totalOtherFees;

  // Total interest
  const numberOfPayments = loanTerm;
  const totalInterest = monthlyPayment * loanTerm;

  // Total cost
  const totalCost = totalInterest + totalLenderCosts;

  // Cash to close
  const cashToClose = downPayment + totalLenderCosts - earnestMoneyDeposit;

  // Monthly PITI
  const piti =
    monthlyPayment +
    monthlyPropertyTaxes +
    monthlyHomeownersInsurance +
    hoaFees;

  return {
    type: USER_LOAN_PROGRAM.BRIDGE,
    offerPrice: offerPrice || undefined,
    listPrice,
    purchasePrice,
    commission,
    marketInterestRate: interestRate,
    creditScore,
    downPayment,
    loanAmount,
    interestRate,
    monthlyPayment,
    monthlyPrincipalInterest: monthlyPayment,
    loanTerm, // Return in months
    balloonPayment,
    numberOfPayments: loanTerm,
    totalInterest,
    totalCost,
    cashToClose,
    piti,
    // Origination fees
    underwritingFee,
    applicationFeeBase,
    processingFeeBase,
    creditReportFeeBase,
    bridgeFee,
    // Other fees
    appraisalFeeBase,
    titleSettlementAgentFee,
    floodDeterminationFee,
    floodMonitoringFee,
    pestInspectionFee,
    surveyFee,
    countyRecordingFees,
    stateRecordingFees,
    transferTaxes,
    mortgageTaxes,
    stateTaxLines: stateTaxes.lines,
    stateTaxState: stateTaxes.state,
    stateTaxNote: stateTaxes.localNote,
    prepaidInterest,
    prepaidsHomeownersInsurance,
    initialEscrowTaxes,
    optionalOwnersTitleInsurance,
    insuranceBinder,
    titleTitleSearchBase,
    titleLendersTitleInsuranceBase,
    recordingTitle,
    recordingMortgage,
    recordingDeedBase,
    exitFee,
    // Totals
    closingCosts: totalLenderCosts,
    totalOriginationFees,
    totalOtherFees,
    totalLenderCosts,
    // Additional fields
    monthlyPropertyTaxes,
    monthlyHomeownersInsurance,
    earnestMoneyDeposit,
    lenderCredits,
    prepaidsPropertyTaxes,
    initialEscrowMortgageInsurance: 0, // Not applicable to Bridge loans
    // Loan type specific
    loanProgram: USER_LOAN_PROGRAM.BRIDGE,
  };
};
